import React, { useState, useEffect } from 'react';
import '../style/Careers.css';
import { TrendingUpIcon, CalendarIcon, CpuIcon, UsersIcon, RocketIcon, BriefcaseIcon, MapPinIcon, ChevronDownIcon, ChevronUpIcon } from '../components/Icons';

const Careers = () => {
    const [jobs, setJobs] = useState([]);
    const [expandedJob, setExpandedJob] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState('');
    const [filterLocation, setFilterLocation] = useState('');

    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/api/careers/jobs`)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch jobs');
                }
                return response.json();
            })
            .then(data => {
                const sortedJobs = [...data].sort((a, b) => b.id - a.id);
                setJobs(sortedJobs);
                setIsLoading(false);
            })
            .catch(err => {
                console.error("Error fetching jobs:", err);
                setError("Unable to load job openings at the moment.");
                setIsLoading(false);
            });
    }, []);

    const toggleJob = (id) => {
        if (expandedJob === id) {
            setExpandedJob(null);
        } else {
            setExpandedJob(id);
        }
    };

    const handleApply = (job) => {
        const subject = encodeURIComponent(`Application for ${job.title} (${job.jobCode || job.id})`);
        const body = encodeURIComponent(`Hi HR Team,\n\nI am interested in applying for the ${job.title} position (#${job.jobCode || job.id}).\n\n[Please attach your resume and add any additional information here]\n\nRegards,`);
        window.location.href = `mailto:hr@kovantech.com?subject=${subject}&body=${body}`;
    };

    const scrollToSection = (className) => {
        const element = document.querySelector(`.${className}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Build unique employment types from loaded jobs for the dropdown
    const employmentTypes = [...new Set(jobs.map(j => j.employmentType).filter(Boolean))];

    // Filter jobs based on selected employment type and typed location
    const filteredJobs = jobs.filter(job => {
        const matchesType     = !filterType     || job.employmentType === filterType;
        const matchesLocation = !filterLocation || job.location.toLowerCase().includes(filterLocation.toLowerCase());
        return matchesType && matchesLocation;
    });

    const isNewJob = (createdAt) => {
        if (!createdAt) return false;
        const jobDate = new Date(createdAt);
        const now = new Date();
        return jobDate.toDateString() === now.toDateString();
    };

    return (
        <div className="careers-container">
            {/* ══ Gradient Hero Section ══ */}
            <section className="careers-hero-section">
                <div className="careers-hero-content">
                    <span className="careers-hero-label">JOIN OUR GROWING TEAM</span>
                    <h1 className="careers-hero-title">
                        Build the Future<br />
                        <span className="careers-hero-highlight">of Tech with Us</span>
                    </h1>
                    <p className="careers-hero-desc">
                        We're more than just a staffing firm. We're a team of innovators and problem solvers shaping the digital landscape.
                    </p>
                    <div className="careers-hero-actions">
                        <button className="btn btn-hero-white" onClick={() => scrollToSection('openings-section')}>View Openings</button>
                        <button className="btn btn-hero-outline" onClick={() => scrollToSection('why-join-section')}>Our Values</button>
                    </div>
                </div>
            </section>

            {/* Why Join Section */}
            <div className="why-join-section">
                <h2>Why Join KovanTech?</h2>
                <p>We provide the environment, tools, and support you need to do the best work of your career.</p>

                <div className="benefits-grid">
                    <div className="benefit-card">
                        <div className="icon-wrapper">
                            <TrendingUpIcon />
                        </div>
                        <h3>Career Growth</h3>
                        <p>Continuous learning paths, certifications, and leadership workshops tailored to you.</p>
                    </div>

                    <div className="benefit-card">
                        <div className="icon-wrapper">
                            <CalendarIcon />
                        </div>
                        <h3>Flexible Work</h3>
                        <p>Hybrid and remote-first options that respect your personal life and productivity style.</p>
                    </div>

                    <div className="benefit-card">
                        <div className="icon-wrapper">
                            <CpuIcon />
                        </div>
                        <h3>Cutting Edge Tech</h3>
                        <p>Work with modern stacks and enterprise-level architecture on mission-critical projects.</p>
                    </div>

                    <div className="benefit-card">
                        <div className="icon-wrapper">
                            <UsersIcon />
                        </div>
                        <h3>Inclusive Culture</h3>
                        <p>A diverse and transparent environment where every developer and designer has a voice.</p>
                    </div>
                </div>
            </div>

            {/* Hiring Journey */}
            <div className="hiring-journey-section">
                <h2>Our Hiring Journey</h2>
                <p>A transparent process from first contact to your first day.</p>

                <div className="process-steps-container">
                    <div className="process-step">
                        <div className="step-circle">01</div>
                        <h4>Apply</h4>
                        <p>Submit your resume</p>
                    </div>
                    <div className="process-step">
                        <div className="step-circle">02</div>
                        <h4>Screening</h4>
                        <p>Recruiter call</p>
                    </div>
                    <div className="process-step">
                        <div className="step-circle">03</div>
                        <h4>Technical</h4>
                        <p>Skill assessment</p>
                    </div>
                    <div className="process-step">
                        <div className="step-circle">04</div>
                        <h4>Final Round</h4>
                        <p>Meet the team</p>
                    </div>
                    <div className="process-step final">
                        <div className="step-circle">
                            <span className="material-symbols-outlined">rocket_launch</span>
                        </div>
                        <h4>Onboarding</h4>
                        <p>Welcome to KovanTech!</p>
                    </div>
                </div>
            </div>

            {/* Open Opportunities */}
            <div className="openings-section">
                <div className="openings-container">
                    <div className="openings-header">
                        <div>
                            <h2>Open Opportunities</h2>
                            <p style={{ color: '#5a7a9a', marginTop: '0.5rem' }}>Explore current openings in engineering, design, and product.</p>
                        </div>
                        <div className="filters">
                            <select
                                className="filter-select"
                                value={filterType}
                                onChange={e => setFilterType(e.target.value)}
                            >
                                <option value="">All Employment Types</option>
                                {employmentTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                className="filter-input"
                                placeholder="Search location..."
                                value={filterLocation}
                                onChange={e => setFilterLocation(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="jobs-list">
                        {isLoading && <p>Loading opportunities...</p>}
                        {error && <p className="error-message">{error}</p>}
                        {!isLoading && !error && filteredJobs.length === 0 && (
                            <p>{jobs.length === 0 ? 'No current openings.' : 'No jobs match your filters.'}</p>
                        )}

                        {filteredJobs.map((job) => (
                            <div
                                key={job.id}
                                className={`job-card ${expandedJob === job.id ? 'expanded' : ''}`}
                                onClick={() => toggleJob(job.id)}
                            >
                                <div className="job-card-header">
                                    <div className="job-id-tag">#{job.jobCode || job.id}</div>
                                    <div className="job-posted-date">RECENTLY POSTED</div>
                                </div>
                                <div className="job-title-row">
                                    <h3>{job.title}</h3>
                                    <div className="job-right-actions">
                                        {isNewJob(job.createdAt) && <span className="new-badge">New</span>}
                                        <div className="job-toggle-icon">
                                            {expandedJob === job.id ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                        </div>
                                    </div>
                                </div>

                                <div className="job-meta">
                                    <span><BriefcaseIcon style={{ width: 16 }} /> {job.employmentType}</span>
                                    <span><MapPinIcon style={{ width: 16 }} /> {job.location}</span>
                                </div>

                                {expandedJob === job.id && (
                                    <div className="job-details-expanded" onClick={(e) => e.stopPropagation()}>
                                        <div className="job-details-grid">
                                            <div className="detail-item">
                                                <label>EXPERIENCE</label>
                                                <span>{job.experienceLevel}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>EMPLOYMENT TYPE</label>
                                                <span>{job.employmentType}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>SALARY RANGE</label>
                                                <span>{job.salaryRange}</span>
                                            </div>
                                        </div>
                                        <div className="job-description">
                                            <h4>Description</h4>
                                            <div
                                                className="description-content"
                                                dangerouslySetInnerHTML={{ __html: job.description }}
                                            />
                                        </div>
                                        <div className="job-action-expanded">
                                            <button className="btn btn-primary" onClick={() => handleApply(job)}>Apply Now</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* General App CTA */}
            <div className="general-app-section">
                <div className="general-app-box">
                    <h3>Don't see a perfect fit?</h3>
                    <p>We're always looking for talented people to join our network. Send us your resume for future opportunities.</p>
                    <button className="btn-outline" onClick={() => handleApply({ title: "Send us your CV", jobCode: "GEN-APP" })}>Send us your CV</button>
                </div>
            </div>
        </div>
    );
};

export default Careers;
